/**
 * Utility functions for handling URL parameters and prefill data
 */

// Keys for localStorage
const PREFILL_KEYS = {
    EMAIL: 'prefill_email',
    FIRST_NAME: 'prefill_firstName',
    LAST_NAME: 'prefill_lastName',
    PROMO_CODE: 'prefill_promoCode'
};

/**
 * Captures URL parameters and stores them in localStorage for prefilling forms
 * Should be called once when the app initializes
 */
export const captureAndStoreUrlParams = () => {
    try {
        const urlParams = new URLSearchParams(window.location.search);
        
        // Capture and decode the parameters
        const rawEmail = urlParams.get('email');
        const rawFirstName = urlParams.get('firstname') || urlParams.get('firstName');
        const rawLastName = urlParams.get('lastname') || urlParams.get('lastName');
        const rawPromoCode = urlParams.get('promocode') || urlParams.get('promoCode') || urlParams.get('promo_code');
        
        // Decode the parameters (URLSearchParams.get() already does basic decoding, but this ensures complete decoding)
        const email = rawEmail ? decodeURIComponent(rawEmail) : null;
        const firstName = rawFirstName ? decodeURIComponent(rawFirstName) : null;
        const lastName = rawLastName ? decodeURIComponent(rawLastName) : null;
        const promoCode = rawPromoCode ? decodeURIComponent(rawPromoCode) : null;
        
        // Store in localStorage if they exist
        if (email) {
            localStorage.setItem(PREFILL_KEYS.EMAIL, email);
        }
        
        if (firstName) {
            localStorage.setItem(PREFILL_KEYS.FIRST_NAME, firstName);
        }
        
        if (lastName) {
            localStorage.setItem(PREFILL_KEYS.LAST_NAME, lastName);
        }
        
        if (promoCode) {
            localStorage.setItem(PREFILL_KEYS.PROMO_CODE, promoCode);
        }
        
        // Clean URL if any prefill params were found
        if (email || firstName || lastName || promoCode) {
            const cleanUrl = new URL(window.location);
            cleanUrl.searchParams.delete('email');
            cleanUrl.searchParams.delete('firstname');
            cleanUrl.searchParams.delete('firstName');
            cleanUrl.searchParams.delete('lastname');
            cleanUrl.searchParams.delete('lastName');
            cleanUrl.searchParams.delete('promocode');
            cleanUrl.searchParams.delete('promoCode');
            cleanUrl.searchParams.delete('promo_code');
            
            // Only update URL if it actually changed
            if (cleanUrl.href !== window.location.href) {
                window.history.replaceState({}, document.title, cleanUrl.href);
            }
        }
    } catch (error) {
        console.error('[URLParams] Error capturing URL parameters:', error);
    }
};

/**
 * Gets prefill data from localStorage
 * @returns {Object} Object with email, firstName, lastName (may be null)
 */
export const getPrefillData = () => {
    try {
        return {
            email: localStorage.getItem(PREFILL_KEYS.EMAIL),
            firstName: localStorage.getItem(PREFILL_KEYS.FIRST_NAME),
            lastName: localStorage.getItem(PREFILL_KEYS.LAST_NAME),
            promoCode: localStorage.getItem(PREFILL_KEYS.PROMO_CODE)
        };
    } catch (error) {
        console.error('[URLParams] Error getting prefill data:', error);
        return {
            email: null,
            firstName: null,
            lastName: null,
            promoCode: null
        };
    }
};

/**
 * Gets a specific prefill value from localStorage
 * @param {string} key - The key to retrieve ('email', 'firstName', 'lastName', or 'promoCode')
 * @returns {string|null} The stored value or null
 */
export const getPrefillValue = (key) => {
    try {
        const keyMap = {
            email: PREFILL_KEYS.EMAIL,
            firstName: PREFILL_KEYS.FIRST_NAME,
            lastName: PREFILL_KEYS.LAST_NAME,
            promoCode: PREFILL_KEYS.PROMO_CODE
        };
        
        const storageKey = keyMap[key];
        if (!storageKey) {
            console.warn(`[URLParams] Invalid prefill key: ${key}`);
            return null;
        }
        
        return localStorage.getItem(storageKey);
    } catch (error) {
        console.error(`[URLParams] Error getting prefill value for ${key}:`, error);
        return null;
    }
};

/**
 * Clears a specific prefill field from localStorage
 * @param {string} key - The key to clear ('email', 'firstName', 'lastName', or 'promoCode')
 */
export const clearPrefillValue = (key) => {
    try {
        const keyMap = {
            email: PREFILL_KEYS.EMAIL,
            firstName: PREFILL_KEYS.FIRST_NAME,
            lastName: PREFILL_KEYS.LAST_NAME,
            promoCode: PREFILL_KEYS.PROMO_CODE
        };
        
        const storageKey = keyMap[key];
        if (!storageKey) {
            console.warn(`[URLParams] Invalid prefill key for clearing: ${key}`);
            return;
        }
        
        localStorage.removeItem(storageKey);
        console.log(`[URLParams] Cleared prefill data for: ${key}`);
    } catch (error) {
        console.error(`[URLParams] Error clearing prefill value for ${key}:`, error);
    }
};

/**
 * Checks if any prefill data exists
 * @returns {boolean} True if any prefill data exists
 */
export const hasPrefillData = () => {
    const data = getPrefillData();
    return !!(data.email || data.firstName || data.lastName || data.promoCode);
};
